import React, { useEffect, useMemo, useState } from 'react';
import { motion, type Variants } from 'framer-motion';
import { footerApi, menuApi } from '../services/api';
import type { FooterSettings } from './Dashboard/types';
import { Globe, Facebook, Instagram, Linkedin, Youtube, Twitter, Github, MessageCircle, Send, MessageSquare } from 'lucide-react';

const letterVariants: Variants = {
  initial: { y: 100, opacity: 0, rotateX: -90 },
  animate: (i: number) => ({
    y: 0,
    opacity: 1,
    rotateX: 0,
    transition: {
      delay: i * 0.08,
      duration: 0.8,
      ease: [0.215, 0.61, 0.355, 1],
    },
  }),
};

const glowVariants: Variants = {
  animate: {
    textShadow: [
      '0 0 10px rgba(255, 191, 0, 0)',
      '0 0 25px rgba(255, 191, 0, 0.6)',
      '0 0 10px rgba(255, 191, 0, 0)',
    ],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  },
};

export const Footer: React.FC<{ onContactClick?: () => void }> = ({ onContactClick }) => {
  type SocialLink = { platform?: string; url?: string };
  type ResourceLink = { title?: string; url?: string };
  type LegalLink = { title?: string; url?: string };
  const [settings, setSettings] = useState<FooterSettings>({});
  const [menuItems, setMenuItems] = useState<{ title: string; url: string }[]>([]);

  const parseArrayValue = <T,>(value: unknown): T[] => {
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') {
      try {
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const socialLinks = useMemo<SocialLink[]>(() => parseArrayValue<SocialLink>(settings?.social_links), [settings]);
  const resourcesLinks = useMemo<ResourceLink[]>(() => parseArrayValue<ResourceLink>(settings?.resources_links), [settings]);
  const legalLinks = useMemo<LegalLink[]>(() => parseArrayValue<LegalLink>(settings?.legal_links), [settings]);
  const brandName = String(settings?.watermark_text || '').trim();
  const normalizeUrl = (raw?: string) => {
    const url = String(raw || '').trim();
    if (!url) return '#';
    if (url.startsWith('/') || url.startsWith('#') || url.startsWith('mailto:') || url.startsWith('tel:')) return url;
    if (/^https?:\/\//i.test(url)) return url;
    if (url.startsWith('//')) return `https:${url}`;
    return `https://${url}`;
  };
  const getSocialIcon = (platform?: string, url?: string) => {
    const source = `${String(platform || '')} ${String(url || '')}`.toLowerCase();
    if (source.includes('facebook')) return Facebook;
    if (source.includes('instagram')) return Instagram;
    if (source.includes('linkedin')) return Linkedin;
    if (source.includes('youtube')) return Youtube;
    if (source.includes('twitter') || source.includes('x.com')) return Twitter;
    if (source.includes('github')) return Github;
    if (source.includes('whatsapp')) return MessageCircle;
    if (source.includes('telegram')) return Send;
    if (source.includes('discord')) return MessageSquare;
    return Globe;
  };

  useEffect(() => {
    const loadLocal = () => {
      try {
        const raw = localStorage.getItem('footerSettings');
        if (!raw) return;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === 'object') {
          setSettings((prev) => ({ ...prev, ...(parsed as FooterSettings) }));
        }
      } catch {
        void 0;
      }
    };

    const load = async () => {
      loadLocal();
      try {
        const r = await footerApi.getSettings();
        if (r?.success) setSettings(r.data || {});
      } catch {
        void 0;
      }
      try {
        const m = await menuApi.list('footer');
        if (m?.success && Array.isArray(m.data)) {
          type MenuNode = { title?: string; url?: string; children?: MenuNode[] };
          const flatten = (nodes: MenuNode[], acc: { title: string; url: string }[] = []) => {
            for (const n of nodes) {
              acc.push({ title: String(n.title || ''), url: String(n.url || '/') });
              if (Array.isArray(n.children)) flatten(n.children, acc);
            }
            return acc;
          };
          setMenuItems(flatten(m.data));
        }
      } catch {
        void 0;
      }
    };

    load();
    const onUpdate = () => {
      loadLocal();
      load();
    };
    window.addEventListener('footer-settings-updated', onUpdate);
    return () => window.removeEventListener('footer-settings-updated', onUpdate);
  }, []);

  return (
    <footer className="relative bg-black pt-20 pb-10 px-6 overflow-hidden border-t border-white/5">
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none opacity-10">
        <div
          className="absolute top-[-10%] right-[-5%] w-[40vw] h-[40vw] rounded-full blur-[150px]"
          style={{ background: `linear-gradient(135deg, ${settings?.gradient_start || '#FFBF00'} 0%, ${settings?.gradient_end || '#FFBF00'} 100%)` }}
        />
      </div>

      <div className="container mx-auto relative z-10">
        <div className="py-20 md:py-40 flex flex-col items-start justify-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="flex items-center gap-4 mb-6"
          >
            <div className="w-12 h-[2px] bg-[#FFBF00]" />
            <span className="text-white/40 font-black uppercase tracking-[0.6em] text-xs md:text-sm">
              {settings?.cta_eyebrow_text || ''}
            </span>
          </motion.div>

          <div className="flex flex-col md:flex-row items-baseline gap-x-6 gap-y-2 flex-wrap">
            <motion.h2
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-5xl md:text-[8vw] font-light text-white tracking-tighter leading-none"
            >
              {settings?.hero_prefix_text || ''}
            </motion.h2>

            <div className="flex overflow-hidden py-4">
              {brandName.split('').map((letter, i) => (
                <motion.span
                  key={i}
                  custom={i}
                  variants={letterVariants}
                  initial="initial"
                  whileInView="animate"
                  viewport={{ once: true }}
                  className="text-5xl md:text-[10vw] font-black text-[#FFBF00] tracking-tighter leading-none inline-block relative group"
                >
                  <motion.span variants={glowVariants} animate="animate">
                    {letter}
                  </motion.span>
                  <motion.span className="absolute inset-0 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none blur-sm">
                    {letter}
                  </motion.span>
                </motion.span>
              ))}
            </div>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8 }}
            className="text-gray-500 text-lg md:text-2xl max-w-xl mt-12 font-light leading-relaxed"
          >
            {settings?.button_description || ''}
          </motion.p>

          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 1 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="mt-12 px-10 py-5 text-white font-black uppercase tracking-widest text-sm rounded-full transition-all"
            style={{
              backgroundImage: `linear-gradient(135deg, ${settings?.button_gradient_start || '#FFBF00'} 0%, ${settings?.button_gradient_end || '#FFBF00'} 100%)`,
              boxShadow: settings?.button_neon_enabled ? `0 0 30px ${settings?.button_neon_color || '#FFBF00'}` : undefined,
              color: settings?.button_text_color || '#ffffff',
            }}
            onClick={() => {
              if (settings?.button_action === 'link' && settings?.button_link) {
                window.open(String(settings.button_link), '_blank');
              } else {
                onContactClick?.();
              }
            }}
          >
            {settings?.button_text || ''}
          </motion.button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-12 pt-20 border-t border-white/5">
          <div className="col-span-2 md:col-span-1">
            <div className="text-2xl font-black uppercase tracking-tighter mb-8">
              {brandName.slice(0, 2)}
              <span className="text-[#FFBF00]">{brandName.slice(2)}</span>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed">
              {settings?.company_description || ''}
            </p>
          </div>

          <div>
            <h4 className="text-white font-bold uppercase text-xs tracking-[0.3em] mb-8">{settings?.navigation_heading_text || ''}</h4>
            <ul className="space-y-4 text-gray-500 text-sm">
              {menuItems.map((m, i) => (
                <li key={i}>
                  <a href={normalizeUrl(m.url)} className="hover:text-[#FFBF00] transition-colors">
                    {m.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold uppercase text-xs tracking-[0.3em] mb-8">{settings?.resources_heading_text || ''}</h4>
            <ul className="space-y-4 text-gray-500 text-sm">
              {resourcesLinks.slice(0, 4).map((l, i: number) => (
                <li key={i}>
                  <a href={normalizeUrl(l.url)} className="hover:text-[#FFBF00] transition-colors">
                    {l.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold uppercase text-xs tracking-[0.3em] mb-8">{settings?.connect_heading_text || ''}</h4>
            <div className="flex gap-4">
              {socialLinks.slice(0, 4).map((s, i: number) => (
                (() => {
                  const Icon = getSocialIcon(s.platform, s.url);
                  return (
                    <motion.a
                      key={i}
                      whileHover={{ y: -5, color: '#FFBF00' }}
                      href={normalizeUrl(s.url)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 border border-white/10 rounded-lg flex items-center justify-center text-gray-400"
                      aria-label={s.platform || 'social'}
                      title={s.platform || 'social'}
                    >
                      <Icon size={16} />
                    </motion.a>
                  );
                })()
              ))}
            </div>
            <p className="mt-8 text-gray-600 text-[10px] uppercase font-bold tracking-widest">
              {settings?.contact_email || ''}
            </p>
            <p className="mt-2 text-gray-600 text-[10px] uppercase font-bold tracking-widest">
              {settings?.contact_phone || ''}
            </p>
          </div>
        </div>

        <div className="mt-20 pt-10 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-6">
          <p className="text-gray-600 text-[10px] uppercase font-bold tracking-widest">
            {settings?.copyright_text || ''}
          </p>
          <div className="flex gap-8">
            {legalLinks.slice(0, 2).map((l, i: number) => (
              <a key={i} href={normalizeUrl(l.url)} className="text-gray-600 text-[10px] uppercase font-bold tracking-widest cursor-pointer hover:text-white transition-colors">
                {l.title}
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};
